import type { HealthLabel, HealthRaw } from "./types";
import { LABEL } from "./terminology";

/**
 * Map raw health strings to canonical labels.
 * States ONLY: Healthy | Degraded | Critical | Unknown (Phase 2).
 *
 * Prefer platform/operations (doctor-derived) health when present;
 * registry last_health_status is fallback.
 */
export function mapHealthLabel(
  status: HealthRaw,
  hasLiveData: boolean,
): HealthLabel {
  if (!hasLiveData || !status) return LABEL.unknown as HealthLabel;
  const s = String(status).trim().toLowerCase();
  if (!s) return LABEL.unknown as HealthLabel;
  if (s === "healthy" || s === "green" || s === "ok" || s === "pass") return "Healthy";
  if (s === "degraded" || s === "yellow" || s === "warn" || s === "warning") return "Degraded";
  if (
    s === "critical" ||
    s === "unhealthy" ||
    s === "red" ||
    s === "fail" ||
    s === "unavailable"
  ) {
    return "Critical";
  }
  return LABEL.unknown as HealthLabel;
}

/**
 * Resolve tenant health with SSoT priority (audit #4):
 * platform ops health first when a scan/stack exists; registry fallback.
 */
export function resolveTenantHealthLabel(opts: {
  opsHealth?: string | null;
  registryHealth?: string | null;
  hasStack?: boolean;
  hasRegistryObservation?: boolean;
}): HealthLabel {
  const hasLive = Boolean(
    opts.hasStack || opts.opsHealth || opts.hasRegistryObservation || opts.registryHealth,
  );
  if (!hasLive) return "Unknown";

  if (opts.opsHealth) {
    return mapHealthLabel(opts.opsHealth, true);
  }
  if (opts.registryHealth) {
    return mapHealthLabel(opts.registryHealth, true);
  }
  return "Unknown";
}

export type HealthToneKey = "success" | "warning" | "danger" | "neutral";

export function healthToneKey(
  label: HealthLabel | "Not monitored" | "Not monitored yet",
): HealthToneKey {
  if (label === "Healthy") return "success";
  if (label === "Degraded") return "warning";
  if (label === "Critical") return "danger";
  return "neutral";
}
